import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { getProfile } from '../api/rttsClient';
import type { Profile } from '../api/types';
import { setMockAuthState } from '../api/mockState';
import { STUDIO_COPY, isStudioUseMock } from '../config';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { useUser } from '../../contexts/UserContext';
import { studioLscHomeUrl } from '../studioLinks';
import StudioHostLink from './StudioHostLink';
import { useStudioMobileApp } from './StudioMobileAppProvider';

function initialsFromProfile(profile: Profile | null, fallback?: string | null): string {
  const source =
    profile?.display_name || profile?.twitch_handle || fallback || '?';
  const parts = String(source).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
  }
  return String(source).slice(0, 2).toUpperCase() || '?';
}

/**
 * Header avatar menu — Account + Sign out. Signed-out: renders nothing.
 */
const StudioAvatarMenu: React.FC = () => {
  const navigate = useNavigate();
  const { status, isLoggedOut, refreshMock } = useStudioAuth();
  const { logout, user } = useUser();
  const mobileApp = useStudioMobileApp();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoggedOut || status === 'loading') {
      setProfile(null);
      return;
    }
    let cancelled = false;
    void getProfile()
      .then((next) => {
        if (!cancelled) setProfile(next);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [status, isLoggedOut]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (isLoggedOut || status === 'loading') {
    return null;
  }

  const avatarUrl = profile?.avatar_url || user?.profile_image_url || null;
  const handle = profile?.twitch_handle || user?.twitch_login || null;
  const initials = initialsFromProfile(profile, handle || user?.display_name);

  const handleSignOut = async () => {
    setOpen(false);
    if (isStudioUseMock()) {
      setMockAuthState('logged_out');
      refreshMock();
    }
    await logout();
    navigate('/studio', { replace: true });
  };

  return (
    <div className="relative" ref={rootRef} data-testid="studio-avatar-menu">
      <button
        type="button"
        className="studio-avatar-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="studio-avatar-menu-trigger"
        title={handle ? `@${handle}` : 'Account'}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="studio-avatar-img studio-avatar-img--sm" />
        ) : (
          <span className="studio-avatar-fallback studio-avatar-fallback--sm" aria-hidden>
            {initials}
          </span>
        )}
      </button>
      {open ? (
        <div
          className="studio-avatar-dropdown"
          role="menu"
          data-testid="studio-avatar-menu-dropdown"
        >
          <Link
            to="/studio/account"
            role="menuitem"
            className="studio-avatar-menu-item no-underline"
            onClick={() => setOpen(false)}
            data-testid="studio-avatar-menu-account"
          >
            {STUDIO_COPY.railAccountLabel}
          </Link>
          <Link
            to="/studio/support"
            role="menuitem"
            className="studio-avatar-menu-item no-underline"
            onClick={() => setOpen(false)}
            data-testid="studio-avatar-menu-support"
          >
            {STUDIO_COPY.reportProblem}
          </Link>
          {user?.rtts_admin === true ? (
            <Link
              to="/studio/admin/support"
              role="menuitem"
              className="studio-avatar-menu-item no-underline"
              onClick={() => setOpen(false)}
              data-testid="studio-avatar-menu-admin-support"
            >
              {STUDIO_COPY.adminSupportTitle}
            </Link>
          ) : null}
          <StudioHostLink
            href={studioLscHomeUrl()}
            className="studio-avatar-menu-item no-underline"
            testId="studio-avatar-menu-lsc"
            onClick={() => setOpen(false)}
          >
            {STUDIO_COPY.railLscLabel}
          </StudioHostLink>
          {mobileApp?.isStandalone ? (
            <span
              className="studio-avatar-menu-item studio-avatar-menu-item--static"
              data-testid="studio-avatar-menu-mobile-app-installed"
            >
              {STUDIO_COPY.mobileAppInstalledLabel}
            </span>
          ) : (
            <button
              type="button"
              role="menuitem"
              className="studio-avatar-menu-item"
              onClick={() => {
                setOpen(false);
                mobileApp?.openInstructions();
              }}
              data-testid="studio-avatar-menu-mobile-app"
            >
              {STUDIO_COPY.mobileAppMenuLabel}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="studio-avatar-menu-item"
            onClick={() => void handleSignOut()}
            data-testid="studio-avatar-menu-sign-out"
          >
            {STUDIO_COPY.accountSignOut}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default StudioAvatarMenu;
